/** Musical time. Integer ticks, PPQ = 480 per quarter note (AGENT.md §11). */
export const PPQ = 480;
/** Step-sequencer resolution: sixteenth notes. */
export const STEP_TICKS = PPQ / 4;

export const MIN_BPM = 30;
export const MAX_BPM = 300;

export interface TimeSignature {
  beatsPerBar: number; // 2–7
  beatUnit: 4; // quarter-note beats only, for now
}

export function secondsPerTick(bpm: number): number {
  if (!(bpm > 0)) throw new RangeError(`Invalid tempo: ${bpm}`);
  return 60 / (bpm * PPQ);
}

export function ticksToSeconds(ticks: number, bpm: number): number {
  return ticks * secondsPerTick(bpm);
}

export function secondsToTicks(seconds: number, bpm: number): number {
  return seconds / secondsPerTick(bpm);
}

export function barTicks(ts: TimeSignature): number {
  return ts.beatsPerBar * PPQ;
}

export function stepsPerBar(ts: TimeSignature): number {
  return barTicks(ts) / STEP_TICKS;
}

export interface MusicalPosition {
  bar: number; // 1-based
  beat: number; // 1-based
  tick: number; // 0..PPQ-1 within the beat
}

export function positionFromTicks(ticks: number, ts: TimeSignature): MusicalPosition {
  const t = Math.max(0, Math.floor(ticks));
  const bar = Math.floor(t / barTicks(ts));
  const inBar = t - bar * barTicks(ts);
  const beat = Math.floor(inBar / PPQ);
  return { bar: bar + 1, beat: beat + 1, tick: inBar - beat * PPQ };
}

export function formatPosition(p: MusicalPosition): string {
  return `${p.bar}.${p.beat}`;
}

/** Snap ticks to the nearest grid line (quantisation). */
export function quantize(ticks: number, gridTicks: number): number {
  if (!(gridTicks > 0)) throw new RangeError("grid must be positive");
  return Math.round(ticks / gridTicks) * gridTicks;
}
